import { useId, useState } from 'react';
import type { Bottle } from '../../shared/api';
import { api, errorMessage } from '../lib/api';
import { defaultRestockAt, describeLevel, isWholeUnit, needsCheck, onShoppingList, unitNoun, unitsLeft } from '../lib/bottles';
import { formatDate } from '../lib/format';
import { BottleGauge } from './BottleGauge';

interface BottleLevelPanelProps {
  bottle: Bottle;
  /** Called with the bottle as the server returns it after a pour or a check. */
  onChange: (bottle: Bottle) => void;
  size?: 'small' | 'large';
}

function pourNoun(bottle: Bottle): string {
  if (isWholeUnit(bottle)) return bottle.kind === 'beer' || bottle.kind === 'cider' ? 'can' : 'one';
  return bottle.serveMl !== null && bottle.serveMl <= 60 ? 'shot' : 'glass';
}

/** The gauge with its pour buttons and the "check the real level" controls. */
export function BottleLevelPanel({ bottle, onChange, size = 'large' }: BottleLevelPanelProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [stocking, setStocking] = useState(false);
  const [restockAt, setRestockAt] = useState(String(bottle.restockAt ?? defaultRestockAt(bottle)));
  const restockId = useId();
  const [checkLevel, setCheckLevel] = useState(Math.round(bottle.level * 100));
  const [checkQuantity, setCheckQuantity] = useState(String(bottle.quantity));
  const levelId = useId();
  const quantityId = useId();
  const whole = isWholeUnit(bottle);
  const noun = pourNoun(bottle);

  async function run(action: () => Promise<Bottle>) {
    setBusy(true);
    setError(null);
    try {
      onChange(await action());
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    } finally {
      setBusy(false);
    }
  }

  function openCheck() {
    setCheckLevel(Math.round(bottle.level * 100));
    setCheckQuantity(String(bottle.quantity));
    setStocking(false);
    setChecking(true);
  }

  function openStocking() {
    setRestockAt(String(bottle.restockAt ?? defaultRestockAt(bottle)));
    setChecking(false);
    setStocking(true);
  }

  async function saveStocking(value: number | null) {
    if (value !== null && !(value >= 0 && value <= 999)) {
      setError('The amount must be a number from 0 to 999.');
      return;
    }
    if (await run(() => api.updateBottle(bottle.id, { restockAt: value }))) setStocking(false);
  }

  async function saveCheck() {
    const quantity = Number(checkQuantity);
    if (!Number.isInteger(quantity) || quantity < 0) {
      setError('How many on hand must be a whole number.');
      return;
    }
    if (await run(() => api.setBottleLevel(bottle.id, whole ? 1 : checkLevel / 100, quantity))) setChecking(false);
  }

  return (
    <div className={`level-panel level-panel--${size}`}>
      <button
        type="button"
        className="level-panel__gauge"
        onClick={openCheck}
        disabled={busy}
        title="Set the real level"
        aria-label="Set the real level"
      >
        <BottleGauge bottle={bottle} size={size} />
      </button>
      <div className="level-panel__body">
        <p className="level-panel__status">{describeLevel(bottle)}</p>
        {needsCheck(bottle) && (
          <p className="notice level-panel__notice">
            Estimate's getting low, last checked {formatDate(bottle.levelCheckedAt)}. Worth a look?
          </p>
        )}
        {bottle.restockAt !== null && onShoppingList(bottle) && !bottle.wantMore && (
          <p className="notice level-panel__notice">
            On the shopping list: {unitsLeft(bottle)} {unitNoun(bottle)}
            {unitsLeft(bottle) === 1 ? '' : 's'} left, kept stocked at {bottle.restockAt}.
          </p>
        )}
        {bottle.quantity > 0 && !checking && !stocking && (
          <div className="pour-buttons" role="group" aria-label={`Record ${noun}s`}>
            {[1, 2, 3].map((count) => (
              <button
                key={count}
                type="button"
                className="btn btn--secondary btn--small"
                disabled={busy}
                onClick={() => void run(() => api.pourBottle(bottle.id, count))}
              >
                {count === 1 ? `−1 ${noun}` : `−${count}`}
              </button>
            ))}
            <button type="button" className="btn btn--ghost btn--small" disabled={busy} onClick={openCheck}>
              Check level
            </button>
          </div>
        )}
        {!checking && !stocking && (
          <div className="pour-buttons">
            <button
              type="button"
              className={`btn btn--small ${bottle.wantMore ? 'btn--primary' : 'btn--secondary'}`}
              aria-pressed={bottle.wantMore}
              disabled={busy}
              onClick={() => void run(() => api.updateBottle(bottle.id, { wantMore: !bottle.wantMore }))}
            >
              {bottle.wantMore ? '✓ Buy again' : 'Buy again?'}
            </button>
            <button
              type="button"
              className={`btn btn--small ${bottle.restockAt !== null ? 'btn--primary' : 'btn--secondary'}`}
              aria-pressed={bottle.restockAt !== null}
              disabled={busy}
              onClick={openStocking}
            >
              {bottle.restockAt !== null ? `✓ Kept stocked (${bottle.restockAt})` : 'Keep stocked'}
            </button>
            <button
              type="button"
              className="btn btn--secondary btn--small"
              disabled={busy}
              title="Bought another one of these"
              onClick={() => void run(() => api.updateBottle(bottle.id, { quantity: bottle.quantity + 1 }))}
            >
              +1 {unitNoun(bottle)}
            </button>
          </div>
        )}
        {stocking && (
          <div className="level-check">
            <label htmlFor={restockId} className="level-check__field level-check__field--inline">
              <span>Add to the shopping list when this many {unitNoun(bottle)}s or fewer are left</span>
              <input
                id={restockId}
                type="number"
                inputMode="decimal"
                min={0}
                max={999}
                step={0.25}
                value={restockAt}
                onChange={(event) => setRestockAt(event.target.value)}
                autoFocus
              />
            </label>
            <p className="muted level-panel__hint">
              {isWholeUnit(bottle) ? 'e.g. 2 means "when I\'m down to two cans".' : 'e.g. 0.25 means "when a quarter of a bottle is left", 1 means "when the last one is opened".'}
            </p>
            <div className="pour-buttons">
              <button type="button" className="btn btn--primary btn--small" disabled={busy} onClick={() => void saveStocking(Number(restockAt))}>
                {busy ? 'Saving…' : 'Save'}
              </button>
              {bottle.restockAt !== null && (
                <button type="button" className="btn btn--secondary btn--small" disabled={busy} onClick={() => void saveStocking(null)}>
                  Stop keeping stocked
                </button>
              )}
              <button type="button" className="btn btn--ghost btn--small" disabled={busy} onClick={() => setStocking(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {checking && (
          <div className="level-check">
            {!whole && (
              <label htmlFor={levelId} className="level-check__field">
                <span>
                  Open bottle: <strong>{checkLevel}%</strong>
                </span>
                <input
                  id={levelId}
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={checkLevel}
                  onChange={(event) => setCheckLevel(Number(event.target.value))}
                />
              </label>
            )}
            <label htmlFor={quantityId} className="level-check__field level-check__field--inline">
              <span>{whole ? 'On hand' : 'Bottles on hand (incl. open)'}</span>
              <input
                id={quantityId}
                type="number"
                inputMode="numeric"
                min={0}
                max={999}
                value={checkQuantity}
                onChange={(event) => setCheckQuantity(event.target.value)}
              />
            </label>
            <div className="pour-buttons">
              <button type="button" className="btn btn--primary btn--small" disabled={busy} onClick={() => void saveCheck()}>
                {busy ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="btn btn--ghost btn--small" disabled={busy} onClick={() => setChecking(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
